<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { PublicVolunteerQualification, VolunteerQualificationPayload } from '@/types/volunteer'
import { VolunteerQualificationType } from '@/types/volunteer'

const props = defineProps<{
  qualifications: PublicVolunteerQualification[]
  modelValue: VolunteerQualificationPayload[]
}>()

const emit = defineEmits<{
  'update:modelValue': [answers: VolunteerQualificationPayload[]]
}>()

const { t } = useI18n()

function getAnswer(categoryId: number): VolunteerQualificationPayload {
  return (
    props.modelValue.find((a) => a.category_id === categoryId) ?? {
      category_id: categoryId,
    }
  )
}

function updateAnswer(categoryId: number, patch: Partial<VolunteerQualificationPayload>): void {
  const existing = getAnswer(categoryId)
  const updated: VolunteerQualificationPayload = { ...existing, ...patch }
  const without = props.modelValue.filter((a) => a.category_id !== categoryId)
  emit('update:modelValue', [...without, updated])
}

function onBooleanChange(categoryId: number, checked: boolean): void {
  updateAnswer(categoryId, { boolean_value: checked })
}

function onSingleChoiceChange(categoryId: number, optionId: number): void {
  updateAnswer(categoryId, { option_ids: [optionId] })
}

function onMultiChoiceChange(categoryId: number, optionId: number, checked: boolean): void {
  const current = getAnswer(categoryId).option_ids ?? []
  const next = checked ? [...current, optionId] : current.filter((id) => id !== optionId)
  updateAnswer(categoryId, { option_ids: next })
}

function onTextChange(categoryId: number, value: string): void {
  updateAnswer(categoryId, { text_value: value })
}

function isOptionSelected(categoryId: number, optionId: number): boolean {
  return (getAnswer(categoryId).option_ids ?? []).includes(optionId)
}

function getBooleanValue(categoryId: number): boolean {
  return getAnswer(categoryId).boolean_value ?? false
}

function getTextValue(categoryId: number): string {
  return getAnswer(categoryId).text_value ?? ''
}
</script>

<template>
  <div class="space-y-5">
    <p class="text-sm text-slate-600">{{ t('volunteer.wizard.step3.qualifications_intro') }}</p>

    <div
      v-for="qual in qualifications"
      :key="qual.id"
      class="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
    >
      <p class="text-sm font-semibold text-slate-700">{{ qual.name }}</p>

      <!-- Boolean -->
      <label
        v-if="qual.type === VolunteerQualificationType.Boolean"
        class="flex items-center gap-2 cursor-pointer"
      >
        <input
          type="checkbox"
          class="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          :checked="getBooleanValue(qual.id)"
          @change="onBooleanChange(qual.id, ($event.target as HTMLInputElement).checked)"
        />
        <span class="text-sm text-slate-700">{{ qual.name }}</span>
      </label>

      <!-- Single choice -->
      <div v-else-if="qual.type === VolunteerQualificationType.SingleChoice" class="space-y-2">
        <label
          v-for="opt in qual.options"
          :key="opt.id"
          class="flex items-center gap-2 cursor-pointer"
        >
          <input
            type="radio"
            :name="`qual-${qual.id}`"
            :value="opt.id"
            class="w-4 h-4 border-slate-300 text-sky-600 focus:ring-sky-500"
            :checked="isOptionSelected(qual.id, opt.id)"
            @change="onSingleChoiceChange(qual.id, opt.id)"
          />
          <span class="text-sm text-slate-700">{{ opt.label }}</span>
        </label>
      </div>

      <!-- Multi choice -->
      <div v-else-if="qual.type === VolunteerQualificationType.MultiChoice" class="space-y-2">
        <label
          v-for="opt in qual.options"
          :key="opt.id"
          class="flex items-center gap-2 cursor-pointer"
        >
          <input
            type="checkbox"
            class="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            :checked="isOptionSelected(qual.id, opt.id)"
            @change="onMultiChoiceChange(qual.id, opt.id, ($event.target as HTMLInputElement).checked)"
          />
          <span class="text-sm text-slate-700">{{ opt.label }}</span>
        </label>
      </div>

      <!-- Text -->
      <textarea
        v-else-if="qual.type === VolunteerQualificationType.Text"
        rows="3"
        maxlength="1000"
        class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        :value="getTextValue(qual.id)"
        @input="onTextChange(qual.id, ($event.target as HTMLTextAreaElement).value)"
      />
    </div>
  </div>
</template>
